import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarPlus, Download } from "lucide-react";
import { ADDRESS, M, EASE } from "../lib/site";
import { Countdown } from "./Countdown";
import { ShareFlyer } from "./ShareFlyer";

const TITLE = "Eleganté Melbourne — Grand Opening";
const DETAILS = "RNB · House · Afro House. Bottomless, live DJs, late nights. 9PM til late.";
const START = "20261113T100000Z"; // 9PM AEDT
const END = "20261113T160000Z";

const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(TITLE)}&dates=${START}/${END}&details=${encodeURIComponent(DETAILS)}&location=${encodeURIComponent(ADDRESS)}`;

const downloadIcs = () => {
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Elegante Melbourne//EN", "BEGIN:VEVENT",
    "UID:elegante-grand-opening-20261113@elegante", `DTSTAMP:${START}`, `DTSTART:${START}`, `DTEND:${END}`,
    `SUMMARY:${TITLE}`, `DESCRIPTION:${DETAILS}`, `LOCATION:${ADDRESS.replace(/,/g, "\\,")}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "elegante-grand-opening.ics";
  a.click();
  URL.revokeObjectURL(url);
};

export const FlyerLightbox = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => { window.removeEventListener("keydown", onKey); window.__lenis?.start(); };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
          onClick={onClose} data-testid="flyer-lightbox" data-lenis-prevent
          className="fixed inset-0 z-[80] flex flex-col items-center overflow-y-auto bg-[#09080B]/92 px-4 py-20 backdrop-blur-xl sm:px-10">
          <button onClick={onClose} aria-label="Close" data-testid="flyer-lightbox-close"
            className="btn-ghost absolute right-4 top-4 rounded-full p-3 text-[#F8F6F0] sm:right-8 sm:top-8"><X size={20} /></button>

          <motion.img src={M.flyer} alt="Eleganté grand opening flyer" onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.92, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="max-h-[55vh] w-auto max-w-[min(100%,1300px)] rounded-2xl shadow-[0_40px_140px_-30px_rgba(230,207,167,0.45)]" />

          <motion.div onClick={(e) => e.stopPropagation()} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
            className="mt-8 flex w-full max-w-[1300px] flex-col items-center justify-between gap-6 sm:flex-row">
            <Countdown testid="lightbox-countdown" />
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <a href={googleUrl} target="_blank" rel="noopener noreferrer" data-testid="save-date-google-btn"
                className="btn-gold inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-[12px] font-medium uppercase tracking-[0.2em]">
                <CalendarPlus size={15} /> Save the date
              </a>
              <button onClick={downloadIcs} data-testid="save-date-ics-btn"
                className="btn-ghost inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-[12px] font-medium uppercase tracking-[0.2em] text-[#F8F6F0]">
                <Download size={15} /> Apple / Outlook
              </button>
            </div>
          </motion.div>
          <motion.div onClick={(e) => e.stopPropagation()} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.3 }}
            className="mt-6 w-full max-w-[1300px] border-t border-[var(--line)] pt-6">
            <ShareFlyer />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
